import { Component, OnInit } from "@angular/core";
import { environment } from "../../environments/environment";
import { AjaxService } from "../services/ajax.service";
import { firstValueFrom, Subject, takeUntil } from "rxjs";
import { EmitterService } from "../services/emitter.service";

declare global {
  interface Window {
    tugiWidget?: {
      initialize: (config: unknown) => void;
      refresh: (config: unknown) => void;
      openWidget?: () => void;
      closeWidget?: () => void;
    };
  }
}

@Component({
  selector: "app-widget",
  standalone: true,
  templateUrl: "./widget.component.html",
  styleUrls: ["./widget.component.scss"],
})
export class WidgetComponent implements OnInit {
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private ajaxService: AjaxService,
    private emitterService: EmitterService,
  ) { }

  ngOnInit(): void {
    this.loadWidget();
    this.emitterService.loginComplete$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reinitializeTugi());

    this.emitterService.logoutComplete$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reinitializeTugi());
  }

  private loadWidget(): void {
    const run = () => this.initializeTugiChat();

    if (window.tugiWidget) {
      run();
      return;
    }

    // If script not in index.html, inject it and wait for onload
    const existing = document.querySelector('script[src*="tugi.widget"]');
    if (existing) {
      existing.addEventListener("load", run);
      return;
    }
    const script = document.createElement("script");
    script.src = environment?.tugiChatConfig?.scriptPath;
    script.charset = "UTF-8";
    // script.crossOrigin = 'anonymous';
    script.onload = () => run();
    document.head.appendChild(script);
  }

  private reinitializeTugi(): void {
    window.tugiWidget?.closeWidget?.();
    window.tugiWidget?.refresh({
      jwtFn: () => this.getJwtFromYourApi(),
      tenantId: environment?.tugiChatConfig?.tenantId,
      brandId: environment?.tugiChatConfig?.brandId,
      brandName: environment?.tugiChatConfig?.brandName,
      useProviderJwt: environment?.tugiChatConfig?.useProviderJwt,
      language: localStorage.getItem("langCode"),
      customize: {
        logo: `${environment.apiUrl}/${environment.mediaPath}/` + "common/" + `${environment.website_name}` + "-logo." + `${environment.website_logo_extension}`,
        primaryColor: environment.tugiChatConfig.theme.primaryColor,
        secondaryColor: environment.tugiChatConfig.theme.secondaryColor,
        homePageBackgroundColor: environment.tugiChatConfig.theme.homePageBackgroundColor,
        homePageTextColor: environment.tugiChatConfig.theme.homePageTextColor,
      },
    });
  }
  private initializeTugiChat(): void {
    window.tugiWidget?.initialize({
      jwtFn: () => this.getJwtFromYourApi(),
      tenantId: environment?.tugiChatConfig?.tenantId,
      brandId: environment?.tugiChatConfig?.brandId,
      brandName: environment?.tugiChatConfig?.brandName,
      useProviderJwt: environment?.tugiChatConfig?.useProviderJwt,
      language: localStorage.getItem("langCode"),
      customize: {
        logo: `${environment.apiUrl}/${environment.mediaPath}/` + "common/" + `${environment.website_name}` + "-logo." + `${environment.website_logo_extension}`,
        primaryColor: environment.tugiChatConfig.theme.primaryColor,
        secondaryColor: environment.tugiChatConfig.theme.secondaryColor,
        homePageBackgroundColor: environment.tugiChatConfig.theme.homePageBackgroundColor,
        homePageTextColor: environment.tugiChatConfig.theme.homePageTextColor,
      },
    });
  }

  private async getJwtFromYourApi(): Promise<string> {
    return (await firstValueFrom(this.ajaxService.generateTugiJwtTocken()))
      .token;
  }
}
